import { noop } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { type TrackSelectionTrigger, useTrackDisabledSelectAll } from '@air/analytics';
import {
  hasSelectedItemsSelector,
  itemIsSelectedSelector,
  preSelectItemsAction,
  resetSelectedItemsAction,
  selectedItemsCountSelector,
  showShiftSelectHighlightsSelector,
} from '@air/redux-selected-items';
import { type Upload } from '@air/redux-uploader';

import { GalleryItemType } from '~/components/Gallery/types';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { type UseHoverType, useHover } from '~/hooks/useHover';
import { setShiftSelectHighlightedGalleryItemsAction } from '~/store/selectedItems/actions';
import { type SelectableGalleryItem } from '~/store/selectedItems/types';
import { useAirSelector, useAirStore } from '~/utils/ReduxUtils';
import { useSelectShiftSelectedHighlightedItems } from './useSelectShiftSelectedHighlightedItems';
import { useToggleSelectedItem } from './useToggleSelectedItem';

/**
 * @property {(trigger: TrackSelectionTrigger, reset?: boolean) => void} handleSelect method to call to mark item as selected. If reset=true, all selected items will be cleared
 */
export type UseMultiselectItemsType = {
  handleSelect: (params: { trigger: TrackSelectionTrigger; reset?: boolean }) => void;
  handlePreSelect: (params: { trigger: TrackSelectionTrigger; reset?: boolean }) => void;
  handleUnselect: (params: { trigger: TrackSelectionTrigger }) => void;
} & UseHoverType;

/**
 * This hook returns info about hovering state and useful method to handle hovering and selecting
 *
 * @param id item's id
 * @param type item's type (board, asset or file)
 * @param data item's data (board object for board, id is enough for other types)
 * @returns {@link UseMultiselectItemsType}
 */
export function useMultiselectItem(
  id: SelectableGalleryItem['id'],
  type: SelectableGalleryItem['type'] | GalleryItemType.upload,
  data: SelectableGalleryItem['item'] | Upload,
): UseMultiselectItemsType {
  const dispatch = useDispatch();
  const store = useAirStore();
  const { trackDisabledSelectAll } = useTrackDisabledSelectAll();
  const isSelected = useAirSelector((st) => itemIsSelectedSelector(st, id));
  const hasSelectedItems = hasSelectedItemsSelector(store.getState());
  const multiselectHighlightIsShowing = showShiftSelectHighlightsSelector(store.getState());
  const selectAll = useViewsSelectAllStore((state) => state.selectAll);
  const resetSelectAll = useViewsSelectAllStore((state) => state.resetSelectAll);
  const { handleHover, handleUnhover, isHovering } = useHover();
  const { toggleSelectedItem } = useToggleSelectedItem();
  const { selectShiftSelectedHighlightedItems } = useSelectShiftSelectedHighlightedItems();

  const item = useMemo(
    () =>
      ({
        type,
        id,
        item: data ? data : { id },
      }) as SelectableGalleryItem,
    [data, id, type],
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: <ignore for now>
  useEffect(() => {
    if (hasSelectedItems && isHovering) {
      dispatch(setShiftSelectHighlightedGalleryItemsAction({ item }));
    }
  }, [isHovering, dispatch, item]);

  const handleSelect = useCallback(
    ({ trigger, reset }: { trigger: TrackSelectionTrigger; reset?: boolean }) => {
      if (multiselectHighlightIsShowing) {
        selectShiftSelectedHighlightedItems(item, trigger);
      } else if (reset) {
        if (selectAll) {
          trackDisabledSelectAll({
            trigger: 'item-click',
            totalSelectionCount: selectedItemsCountSelector(store.getState()),
          });
        }
        resetSelectAll();
        dispatch(resetSelectedItemsAction());
        if (trigger !== 'double-click') {
          toggleSelectedItem({ item, trigger });
        }
      } else if (trigger !== 'double-click') {
        toggleSelectedItem({ item, trigger });
      }
    },
    [
      multiselectHighlightIsShowing,
      selectShiftSelectedHighlightedItems,
      item,
      dispatch,
      toggleSelectedItem,
      resetSelectAll,
      selectAll,
      store,
      trackDisabledSelectAll,
    ],
  );

  const handlePreSelect = useCallback(
    ({ reset }: { trigger: TrackSelectionTrigger; reset?: boolean }) => {
      if (reset && selectAll) {
        trackDisabledSelectAll({
          trigger: 'item-click',
          totalSelectionCount: selectedItemsCountSelector(store.getState()),
        });
        resetSelectAll();
        dispatch(resetSelectedItemsAction());
      }
      if (!isSelected) {
        reset && dispatch(resetSelectedItemsAction());
        dispatch(preSelectItemsAction({ itemsIds: [item.id] }));
      }
    },
    [dispatch, isSelected, item.id, resetSelectAll, selectAll, store, trackDisabledSelectAll],
  );

  const handleUnselect = useCallback(
    ({ trigger }: { trigger: TrackSelectionTrigger }) => {
      if (trigger !== 'double-click') {
        toggleSelectedItem({ item, trigger, forceToggle: 'unselect' });
      }
    },
    [item, toggleSelectedItem],
  );

  if (type === GalleryItemType.upload) {
    return {
      isHovering,
      handleHover,
      handleUnhover,
      handlePreSelect: noop,
      handleSelect: noop,
      handleUnselect: noop,
    };
  }

  return { isHovering, handleHover, handleUnhover, handleSelect, handleUnselect, handlePreSelect };
}
